import LoadingBar from "@components/ui/LoadingBar";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import DataGrid from "@components/ui/tables/DataGrid";
import { transformOrdresToTable } from "@components/ui/tables/transformToTables";
import { useDataStore } from "@store/user_org";
import { useOrdreStore } from "@store/ordreStore";
import { isOwnerOrAdmin, useSimulateMemberView } from "@utils/permissions";
import { ordresApi } from "@services/api/ordres";
import { statusChangeUpdates, apiErrorMessage } from "@store/arretJustificationStore";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import { searchOrdresFull } from "@services/algolia";
import OrdreStatusGuide from "@components/ui/OrdreStatusGuide";
import OrdreHoverPanel from "@components/ui/OrdreHoverPanel";
import { useTagStore } from "@store/tagStore";
import { useSearchParams } from "react-router-dom";
import { ORDRE_FLAGS, ORDRE_FLAG_CONFIG, type OrdreFlag, type OrdreStatus, type TrackingStep } from "types/ordre";
import ChatPanel from "@components/chat/ChatPanel";
import { useMessagerieStore } from "@store/messagerieStore";
import { messagerieApi } from "@services/api/messagerie";
import { Switch } from "@components/ui/selection/Switch";
import { deleteOrdreInBackground } from "@services/ordreDelete";
import TagCategoryGroups from "@components/ui/tags/TagCategoryGroups";

const STATUS_TO_TRACKING: Partial<Record<OrdreStatus, TrackingStep>> = {
  a_preparer: "en_attente_depot",
  devis_initial: "devis_pret",
  commande: "en_attente_depot",
  pret: "en_attente_depot",
  reception: "vehicule_depose",
  en_cours: "reparations_en_cours",
  devis_supp: "devis_supplementaire",
  accord: "reparations_en_cours",
  acompte: "acompte_manquant",
  fin_de_travaux: "travaux_termines",
  facturation: "facture_disponible",
  solde: "solde_en_attente",
  solde_paye: "paiement_valide",
  disponible: "vehicule_disponible",
  restitue: "vehicule_restitue",
};

const statusTabs = [
  { value: "all", label: "Tous" },
  { value: "active", label: "Actifs" },
  { value: "rdv", label: "RDV" },
  { value: "a_preparer", label: "A préparer" },
  { value: "devis_initial", label: "Devis initial" },
  { value: "commande", label: "Commande" },
  { value: "pret", label: "Prêt" },
  { value: "reception", label: "Réceptionné" },
  { value: "en_cours", label: "En cours" },
  { value: "devis_supp", label: "Devis supp." },
  { value: "accord", label: "Accord" },
  { value: "acompte", label: "Acompte" },
  { value: "fin_de_travaux", label: "Fin de travaux" },
  { value: "facturation", label: "Facturation" },
  { value: "solde", label: "Solde" },
  { value: "solde_paye", label: "Solde payé" },
  { value: "disponible", label: "Disponible" },
  { value: "restitue", label: "Restitué" },
  { value: "arrete", label: "Arrêté" },
  { value: "annule", label: "Annulé" },
];

// Local-storage persisted screen filters (besides myOnly, which lives in the store).
const ORDRES_FILTERS_KEY = "ordres-filters";
const _savedFilters: {
  tagFilter?: string[]; flagFilter?: string[]; flagBloque?: boolean; flagUrgent?: boolean; dateRange?: string; eventType?: string;
} = (() => {
  try { return JSON.parse(localStorage.getItem(ORDRES_FILTERS_KEY) || "{}"); } catch { return {}; }
})();

const DATE_RANGES = [
  { value: "today", label: "Aujourd'hui" },
  { value: "week", label: "Cette semaine" },
  { value: "next5", label: "5 prochains jours" },
];

// [startMs, endMs) for a date-range key, or null.
function ordresDateWindow(range: string): [number, number] | null {
  const DAY = 86400000;
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (range === "today") return [start, start + DAY];
  if (range === "next5") return [start, start + 5 * DAY];
  if (range === "week") {
    const dow = (new Date(start).getDay() + 6) % 7; // 0 = Monday
    const monday = start - dow * DAY;
    return [monday, monday + 7 * DAY];
  }
  return null;
}

export default function OrdresScreen() {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  useSimulateMemberView();
  // Anyone can delete ordres (manager-only rule removed).
  const canDelete = true;
  const {
    ordres,
    isLoading,
    isRefreshing,
    hasMore,
    hasPrev,
    myOnly,
    totalCount,
    page,
    fetchOrdres,
    fetchNextPage,
    fetchPrevPage,
    goToPage,
    setSort,
    setMyOnly,
    removeOrdres,
  } = useOrdreStore();

  const [searchParams, setSearchParams] = useSearchParams();

  const [table, setTable] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Same switch as the single-ordre deletion: on by default, only shown when the selection
  // holds at least one RDV that Carvis created on ServiceBox.
  const [deleteInServiceBox, setDeleteInServiceBox] = useState(true);
  const [activeTab, setActiveTab] = useState<string>(() => {
    const param = searchParams.get("status");
    // Default to "Actifs" (hide the flood of restitued/closed ordres); an explicit
    // ?status= link (dashboard cards, etc.) still wins.
    return param && statusTabs.some((t) => t.value === param) ? param : "active";
  });
  const [tagFilter, setTagFilter] = useState<string[]>(() => _savedFilters.tagFilter || []);
  // Drapeaux filtrés (ET entre eux). `?flag=` d'un lien (dashboard) prime sur le filtre
  // mémorisé ; les anciens booléens flagBloque/flagUrgent du localStorage sont repris.
  const [flagFilter, setFlagFilter] = useState<OrdreFlag[]>(() => {
    const fromUrl = searchParams.get("flag");
    if (fromUrl && (ORDRE_FLAGS as string[]).includes(fromUrl)) return [fromUrl as OrdreFlag];
    const saved = (_savedFilters.flagFilter || []).filter((f): f is OrdreFlag => (ORDRE_FLAGS as string[]).includes(f));
    if (saved.length) return saved;
    return [...(_savedFilters.flagBloque ? (["bloque"] as OrdreFlag[]) : []), ...(_savedFilters.flagUrgent ? (["urgent"] as OrdreFlag[]) : [])];
  });
  const toggleFlag = (f: OrdreFlag) => setFlagFilter((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));
  // Date-window + event-type filters (réception/restitution), persisted below.
  const [dateRange, setDateRange] = useState<string>(() => _savedFilters.dateRange || ""); // "" | today | week | next5
  const [eventType, setEventType] = useState<string>(() => _savedFilters.eventType || ""); // "" | reception | restitution
  const [chatOrdre, setChatOrdre] = useState<{ id: string; discussion_id?: string; internal_discussion_id?: string; name: string; phone?: string } | null>(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const filterBtnRef = useRef<HTMLDivElement>(null);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const [filterPos, setFilterPos] = useState({ top: 0, left: 0 });

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === "all") {
      searchParams.delete("status");
    } else {
      searchParams.set("status", tab);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const handleTagToggle = (tagId: string) => {
    setTagFilter((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  useEffect(() => {
    if (orgData?.id) {
      useTagStore.getState().fetchTags(orgData.id);
      const assignedTo = myOnly ? userData?.id : undefined;
      // Date window filtered SERVER-SIDE (whole base, not just the loaded page).
      const win = ordresDateWindow(dateRange);
      const dateOpts = win
        ? { start: Math.floor(win[0] / 1000), end: Math.floor(win[1] / 1000), field: eventType || undefined }
        : undefined;
      fetchOrdres(orgData.id, activeTab, assignedTo, dateOpts);
    }
    // userData?.id is in the deps: "Mes ordres" (myOnly) is restored from
    // persisted prefs, but userData can load a tick after orgData — without this
    // the filter stays checked yet fetches unfiltered (and never re-fetches).
  }, [orgData?.id, activeTab, myOnly, userData?.id, dateRange, eventType]);

  // Close filter panel on outside click
  useEffect(() => {
    if (!showFilterPanel) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (filterBtnRef.current?.contains(target)) return;
      if (filterPanelRef.current?.contains(target)) return;
      setShowFilterPanel(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilterPanel]);

  // Preload discussion into messagerie store when chat opens
  useEffect(() => {
    if (!chatOrdre?.discussion_id) return;
    const discId = chatOrdre.discussion_id;
    const already = useMessagerieStore.getState().discussions.find((d) => d.id === discId);
    if (already) return;
    messagerieApi.getDiscussion(discId).then((disc) => {
      useMessagerieStore.setState((s) => ({
        discussions: [...s.discussions, disc],
      }));
    }).catch(() => {});
  }, [chatOrdre?.discussion_id]);

  const activeFilterCount = (myOnly ? 1 : 0) + flagFilter.length + tagFilter.length + (dateRange ? 1 : 0) + (eventType ? 1 : 0);
  const tags = useTagStore((s) => s.tags).filter((t) => t.scope === "ordre" && t.active).sort((a, b) => a.name.localeCompare(b.name));

  // Persist screen filters in localStorage.
  useEffect(() => {
    localStorage.setItem(ORDRES_FILTERS_KEY, JSON.stringify({ tagFilter, flagFilter, dateRange, eventType }));
  }, [tagFilter, flagFilter, dateRange, eventType]);

  const filteredOrdres = useMemo(() => {
    let result = ordres;
    for (const flag of flagFilter) {
      result = result.filter((o) => o.flags && o.flags.includes(flag));
    }
    // Date window (réception/restitution) is applied SERVER-SIDE via fetchOrdres.
    if (tagFilter.length === 0) return result;
    return result.filter((o) =>
      (o.tag_ids || []).some((id) => tagFilter.includes(id))
    );
  }, [ordres, tagFilter, flagFilter]);

  const handleTagsChange = async (ordreId: string, tagIds: string[]) => {
    try {
      const updated = await ordresApi.update(ordreId, { tag_ids: tagIds });
      useOrdreStore.getState().updateOrdre(updated);
    } catch {
      showNotification({ message: "Erreur lors de la mise à jour des tags", type: "error" });
    }
  };

  const handleStatusChange = async (ordreId: string, status: OrdreStatus) => {
    // Arrêter demande un motif (un tag suffit) ; annulé = statut inchangé.
    const extra = await statusChangeUpdates(ordreId, status, ordres.find((o) => o.id === ordreId));
    if (!extra) return;
    try {
      const updates: any = { status, ...extra };
      const autoTracking = STATUS_TO_TRACKING[status];
      if (autoTracking) updates.tracking_step = autoTracking;
      const updated = await ordresApi.update(ordreId, updates);
      useOrdreStore.getState().updateOrdre(updated);
    } catch (e) {
      showNotification({ message: apiErrorMessage(e, "Erreur lors de la mise à jour du statut"), type: "error" });
    }
  };

  useEffect(() => {
    const table = transformOrdresToTable(filteredOrdres, "all", handleTagsChange, handleStatusChange, setChatOrdre);
    setTable(table);
  }, [filteredOrdres]);

  const selectedOrdres = useMemo(() => ordres.filter((o) => selectedIds.includes(o.id)), [ordres, selectedIds]);
  const selectedOnServiceBox = selectedOrdres.filter((o) => !!o.servicebox_rdv_id);

  const handleDeleteSelected = () => {
    const sb = orgData?.servicebox;
    const sbCreds = sb ? { username: sb.username || "", password: sb.password || "", site_code: sb.site_code, operator_email: userData?.infos?.email || "" } : undefined;
    // Ordres with a RDV on ServiceBox go through the background deleter (RDV cancelled via
    // the garage's proxy, then the ordre, one task pill each); the others are dropped at once.
    const viaProxy = deleteInServiceBox && sbCreds ? selectedOnServiceBox : [];
    const viaProxyIds = new Set(viaProxy.map((o) => o.id));
    const direct = selectedIds.filter((id) => !viaProxyIds.has(id));
    if (direct.length) removeOrdres(direct);
    if (viaProxy.length) {
      useOrdreStore.setState((s) => ({ ordres: s.ordres.filter((o) => !viaProxyIds.has(o.id)) }));
      viaProxy.forEach((ordre) => deleteOrdreInBackground({ ordre, deleteInServiceBox: true, sbCreds }));
    }
    showNotification({
      type: "success",
      message: viaProxy.length
        ? `${selectedIds.length} ordre(s) supprimé(s) — ${viaProxy.length} RDV ServiceBox en cours d'annulation`
        : `${selectedIds.length} ordre(s) supprimé(s)`,
    });
    setSelectedIds([]);
    setShowDeleteConfirm(false);
  };

  const handleMyOnlyToggle = (e: any) => {
    setMyOnly(e.target.checked);
  };


  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {showDeleteConfirm && (
        <ConfirmationModal
          title={`Supprimer ${selectedIds.length} ordre(s) ?`}
          message="Cette action est irréversible. Les ordres et toutes leurs données seront définitivement supprimés."
          onClose={() => setShowDeleteConfirm(false)}
          extraContent={selectedOnServiceBox.length > 0 ? (
            <div className="flex flex-row items-center gap-3 p-2.5 rounded-lg bg-neutral-50 border border-neutral-200">
              <div className="flex flex-col flex-1">
                <span className="text-h4 font-medium text-neutral-800">Supprimer aussi dans ServiceBox</span>
                <span className="text-h5 text-neutral-400">
                  {selectedOnServiceBox.length === 1
                    ? "Annule également le RDV dans le planning ServiceBox"
                    : `Annule également les ${selectedOnServiceBox.length} RDV dans le planning ServiceBox`}
                </span>
              </div>
              <Switch checked={deleteInServiceBox} onChange={setDeleteInServiceBox} />
            </div>
          ) : undefined}
          onConfirm={handleDeleteSelected}
          confirmText="CONFIRMER"
        />
      )}

      {/* Header */}
      <div className="flex-shrink-0 pt-10 px-4 lg:px-10 pb-4">
        <div className="flex flex-col md:flex-row w-full max-w-1p2k justify-between">
          <div className="flex flex-col w-full md:w-500">
            <Text1>Ordres de réparation</Text1>
            <Text2>Retrouvez tous vos ordres de réparations ici.</Text2>
          </div>
          <div className="flex flex-row gap-2 items-center">
            <OrdreStatusGuide />
            {canDelete && selectedIds.length > 0 && (
              <SecondaryButton
                startIcon={<Icon1 name="delete" size={18} />}
                onClick={() => setShowDeleteConfirm(true)}
              >
                {`Supprimer la sélection (${selectedIds.length})`}
              </SecondaryButton>
            )}
          </div>
        </div>
      </div>

      {/* DataGrid area */}
      <div className="flex-1 min-h-0 px-4 lg:px-10 pb-6 flex flex-col gap-3 w-full">
        {(isLoading || isRefreshing) && <LoadingBar />}
        <div className="flex-1 min-h-0 w-full">
            {table && (
              <DataGrid
                storageKey="ordres"
                serverSideSorted
                selectable={canDelete}
                table={table}
                hide_lines={true}
                initial_saved_columns_showed={{ id: false, created_at: false }}
                tabs={statusTabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                onSearch={async (query) => {
                  if (!orgData?.id) return [];
                  return searchOrdresFull(query, orgData.id);
                }}
                onSearchSelect={(option) => navigateTo({ route: "/ordre/" + option.value })}
                onClick={(item) => navigateTo({ route: "/ordre/" + item.id })}
                renderRowHover={(row) => <OrdreHoverPanel row={row} />}
                handleSelectionModelChange={(ids) => setSelectedIds(ids)}
                hasNext={hasMore}
                hasPrev={hasPrev}
                onNext={() => orgData?.id && fetchNextPage(orgData.id)}
                onPrev={() => orgData?.id && fetchPrevPage(orgData.id)}
                totalRows={totalCount}
                serverPage={page}
                totalServerPages={Math.ceil(totalCount / 25)}
                onServerPageChange={(p) => orgData?.id && goToPage(orgData.id, p)}
                handleSortChange={(model) => {
                  if (orgData?.id && model.length > 0)
                    setSort(orgData.id, model[0].field, model[0].sort);
                }}
                extraSearchContent={
                  <div ref={filterBtnRef}>
                    <SecondaryButton
                      startIcon={<Icon1 name="filters" size={16} color={activeFilterCount > 0 ? "#2563EB" : "#525252"} />}
                      onClick={() => {
                        if (filterBtnRef.current) {
                          const rect = filterBtnRef.current.getBoundingClientRect();
                          setFilterPos({ top: rect.bottom + 4, left: rect.left });
                        }
                        setShowFilterPanel((v) => !v);
                      }}
                    >
                      <span className="flex items-center gap-1.5">
                        Filtres
                        {activeFilterCount > 0 && (
                          <span className="bg-blue-500 text-white rounded-full text-h5 w-4 h-4 flex items-center justify-center">
                            {activeFilterCount}
                          </span>
                        )}
                      </span>
                    </SecondaryButton>
                  </div>
                }
              />
            )}
          </div>
      </div>

      {/* Floating chat panel */}
      {chatOrdre && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col border rounded-[15px] overflow-hidden shadow-xl" style={{ width: 400, height: 600, maxHeight: "calc(100vh - 48px)", background: "#FFFFFF", borderColor: "#D9D9D9" }}>
          <div className="flex flex-row items-center justify-between px-4 py-2 border-b border-neutral-200">
            <span className="text-h2 font-medium truncate">{chatOrdre.name || "Conversation"}</span>
            <button onClick={() => setChatOrdre(null)} className="hover:bg-neutral-100 rounded-full p-1 flex-shrink-0">
              <svg width={12} height={12} viewBox="0 0 12 12" fill="none">
                <path d="M1 1L11 11M11 1L1 11" stroke="#A1A1A1" strokeWidth={1.5} strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <ChatPanel
              discussionId={chatOrdre.discussion_id}
              internalDiscussionId={chatOrdre.internal_discussion_id}
              entityType="ordre"
              entityId={chatOrdre.id}
              entityName={chatOrdre.name}
              members={[]}
              onlineCount={0}
              compact
              whatsappProvider={orgData?.whatsapp?.provider || "wasender"}
            />
          </div>
        </div>
      )}

      {/* Filter panel (portal) */}
      {showFilterPanel && createPortal(
        <div
          ref={filterPanelRef}
          className="bg-white shadow-lg rounded-xl border flex flex-col"
          style={{ position: "fixed", top: filterPos.top, left: filterPos.left, zIndex: 10001, width: 280 }}
        >
          {/* Mes ordres */}
          <div className="px-3 py-2.5 border-b">
            <CheckBoxInput
              checked={myOnly}
              onChange={handleMyOnlyToggle}
              label="Mes ordres"
            />
          </div>
          {/* Période (réception / restitution) */}
          <div className="px-3 py-2.5 border-b flex flex-col gap-2">
            <div className="text-h4 text-neutral-400">Période</div>
            <div className="flex flex-row flex-wrap gap-2">
              {DATE_RANGES.map((r) => {
                const active = dateRange === r.value;
                return (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setDateRange(active ? "" : r.value)}
                    className="px-2.5 py-1 rounded-full text-h4 font-medium transition-colors"
                    style={{ background: active ? "#000" : "#F5F5F5", color: active ? "#FFFFFF" : "#333" }}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-row gap-2">
              {([["reception", "Réceptions"], ["restitution", "Restitutions"]] as const).map(([val, lbl]) => {
                const active = eventType === val;
                return (
                  <button
                    key={val}
                    type="button"
                    disabled={!dateRange}
                    onClick={() => setEventType(active ? "" : val)}
                    className="px-2.5 py-1 rounded-full text-h4 font-medium transition-colors"
                    style={{
                      background: active ? "#6366F1" : "#EEF2FF",
                      color: active ? "#FFFFFF" : "#4338CA",
                      opacity: dateRange ? 1 : 0.45,
                      cursor: dateRange ? "pointer" : "not-allowed",
                    }}
                  >
                    {lbl}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Drapeaux — les quatre, dans l'ordre de gravité de ORDRE_FLAGS */}
          <div className="px-3 py-2.5 border-b flex flex-col gap-2">
            <div className="text-h4 text-neutral-400">Drapeaux</div>
            <div className="flex flex-row flex-wrap gap-2">
              {ORDRE_FLAGS.map((flag) => {
                const cfg = ORDRE_FLAG_CONFIG[flag];
                const on = flagFilter.includes(flag);
                return (
                  <button
                    key={flag}
                    type="button"
                    onClick={() => toggleFlag(flag)}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-h4 font-medium transition-colors"
                    style={{ background: on ? cfg.color : cfg.bg, color: on ? "#FFFFFF" : cfg.color }}
                  >
                    <Icon1 name={cfg.icon} size={12} color={on ? "#FFFFFF" : cfg.color} />
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Tags */}
          {tags.length > 0 && (
            <div className="px-3 py-2.5 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-h4 text-neutral-400">Tags</span>
                {tagFilter.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setTagFilter([])}
                    className="text-h5 text-neutral-400 hover:text-neutral-600"
                  >
                    Effacer
                  </button>
                )}
              </div>
              <div className="flex flex-row flex-wrap gap-1.5">
                <TagCategoryGroups tags={tags} renderTag={(tag) => {
                  const isSelected = tagFilter.includes(tag.id);
                  return (
                    <span
                      key={tag.id}
                      className="text-h4 whitespace-nowrap cursor-pointer select-none"
                      onClick={() => handleTagToggle(tag.id)}
                      style={{
                        background: isSelected ? tag.color : "transparent",
                        color: isSelected ? "#FFFFFF" : tag.color,
                        border: `1.5px solid ${tag.color}`,
                        borderRadius: 30,
                        padding: "2px 10px",
                        lineHeight: "16px",
                        opacity: isSelected ? 1 : 0.7,
                      }}
                    >
                      {tag.name}
                    </span>
                  );
                }} />
              </div>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
